import React, { useState } from 'react';
import { useDoc, useEditorActions } from '../store/editorStore.jsx';
import { BlockRenderer } from './BlockRenderer.jsx';
import { Inserter } from './Inserter.jsx';
import { DND_BLOCK } from '../dnd/dndTypes.js';

/**
 * RootContainer
 *
 * Renders the ordered list of top-level blocks from doc.root.
 * Handles drop events at the root level (after the last block).
 */
export function RootContainer() {
  const doc = useDoc();
  const { moveBlock, insertBlock, deselect } = useEditorActions();
  const [inserterOpen, setInserterOpen] = useState(false);
  const [rootDragOver, setRootDragOver] = useState(false);

  const { root } = doc;

  const handleRootDragOver = (e) => {
    if (!e.dataTransfer.types.includes(DND_BLOCK)) return;
    // Only respond if the target is the root itself (not a child block)
    if (e.currentTarget !== e.target && e.currentTarget.contains(e.target)) {
      const closest = e.target.closest('[data-block-id]');
      if (closest) return; // Let the block frame handle it
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setRootDragOver(true);
  };

  const handleRootDrop = (e) => {
    if (!e.dataTransfer.types.includes(DND_BLOCK)) return;
    e.preventDefault();
    setRootDragOver(false);
    const draggedId = e.dataTransfer.getData(DND_BLOCK);
    if (!draggedId) return;
    // Drop at the end of root
    moveBlock(draggedId, null, root.length);
  };

  return (
    <div
      className="editor-root-container"
      style={{ minHeight: 200, padding: '8px 0' }}
      onClick={(e) => {
        // Deselect if clicking on empty canvas space
        if (e.target === e.currentTarget) deselect();
      }}
      onDragOver={handleRootDragOver}
      onDragLeave={() => setRootDragOver(false)}
      onDrop={handleRootDrop}
    >
      {root.length === 0 ? (
        /* Empty state */
        <div
          className="text-center p-5"
          style={{
            border: '2px dashed #dee2e6',
            borderRadius: 8,
            background: rootDragOver ? '#e8f0fe' : '#fafafa',
            transition: 'background 0.15s',
          }}
        >
          <div style={{ fontSize: '2.5rem' }}>📝</div>
          <p className="text-muted mb-3 mt-2">
            Your content is empty. Add a block to get started.
          </p>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={(e) => { e.stopPropagation(); setInserterOpen(true); }}
            >
              + Add Block
            </button>
            {inserterOpen && (
              <div style={{ position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}>
                <Inserter
                  parentId={null}
                  index={0}
                  onInsert={(type) => { insertBlock(null, 0, type); setInserterOpen(false); }}
                  onClose={() => setInserterOpen(false)}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {root.map((blockId, index) => (
            <BlockRenderer
              key={blockId}
              blockId={blockId}
              doc={doc}
              parentId={null}
              index={index}
              siblingCount={root.length}
            />
          ))}

          {/* "Add block" button at the end */}
          <div
            className="text-center mt-3"
            style={{ position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setInserterOpen((v) => !v)}
            >
              + Add Block
            </button>
            {inserterOpen && (
              <div style={{ position: 'absolute', top: '110%', left: '50%', transform: 'translateX(-50%)', zIndex: 100 }}>
                <Inserter
                  parentId={null}
                  index={root.length}
                  onInsert={(type) => { insertBlock(null, root.length, type); setInserterOpen(false); }}
                  onClose={() => setInserterOpen(false)}
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
